import { HttpErrorResponse } from '@angular/common/http';

import { ApiError } from '@app/core/models';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeErrors = (value: unknown): Record<string, string[]> | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const entries = Object.entries(value)
    .map(([key, item]) => {
      if (Array.isArray(item)) {
        return [key, item.filter((entry): entry is string => typeof entry === 'string')];
      }

      if (typeof item === 'string') {
        return [key, [item]];
      }

      return [key, []];
    })
    .filter(([, messages]) => messages.length > 0);

  return entries.length ? Object.fromEntries(entries) : undefined;
};

const readString = (source: Record<string, unknown> | undefined, key: string): string | undefined => {
  const value = source?.[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
};

export const parseApiError = (error: unknown, fallbackMessage: string): ApiError => {
  if (!(error instanceof HttpErrorResponse)) {
    return { message: fallbackMessage };
  }

  const payload = error.error;

  if (typeof payload === 'string' && payload.trim()) {
    return {
      message: payload,
      status: error.status
    };
  }

  if (isRecord(payload)) {
    const nestedError = isRecord(payload['error']) ? payload['error'] : undefined;

    return {
      message:
        readString(nestedError, 'message') ||
        readString(payload, 'message') ||
        readString(payload, 'title') ||
        fallbackMessage,
      code: readString(nestedError, 'code') || readString(payload, 'code'),
      status: error.status,
      errors: normalizeErrors(payload['errors']) ?? normalizeErrors(nestedError?.['details'])
    };
  }

  return {
    message: fallbackMessage,
    status: error.status
  };
};

export const getFieldApiError = (
  apiError: ApiError | null,
  fieldName: string
): string | null => apiError?.errors?.[fieldName]?.[0] ?? null;
